import type { Lang } from "@/lib/types";

/**
 * Resolves page-copy JSON trees (`src/data/copy/*.json`) for one language.
 *
 * Convention (same as `{ "en": "…", "sv": "…" }` used across `src/data/`):
 * an object whose keys are all language codes (and that has an `en` value) is
 * a *localized leaf*. The leaf value may be a string, a string array, or a
 * plain structure (e.g. FAQ items). Everything else is walked recursively, so
 * non-translatable fields (ids, coordinates, numbers) stay as plain values.
 */

const LANG_KEYS: ReadonlySet<string> = new Set<Lang>([
  "en",
  "sv",
  "no",
  "de",
  "fr",
  "es",
  "it",
  "nl",
  "pt",
  "da",
  "fi",
  "ru",
]);

/** Type-level mirror of {@link localizeCopy}: picks the leaf value, maps everything else. */
export type Localized<T> = T extends readonly (infer U)[]
  ? Localized<U>[]
  : T extends { en: infer E }
    ? keyof T extends Lang
      ? E
      : { [K in keyof T]: Localized<T[K]> }
    : T extends object
      ? { [K in keyof T]: Localized<T[K]> }
      : T;

function isLocalizedLeaf(value: object): value is Record<string, unknown> {
  const keys = Object.keys(value);
  return keys.includes("en") && keys.every((key) => LANG_KEYS.has(key));
}

export function localizeCopy<T>(tree: T, lang: Lang = "sv"): Localized<T> {
  if (Array.isArray(tree)) {
    return tree.map((item) => localizeCopy(item, lang)) as Localized<T>;
  }
  if (tree !== null && typeof tree === "object") {
    if (isLocalizedLeaf(tree)) {
      return (tree[lang] ?? tree.en) as Localized<T>;
    }
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(tree)) {
      out[key] = localizeCopy(value, lang);
    }
    return out as Localized<T>;
  }
  return tree as Localized<T>;
}

/** Memoised per-language getter for a JSON copy tree. */
export function createCopyGetter<T>(tree: T): (lang: Lang) => Localized<T> {
  const cache = new Map<Lang, Localized<T>>();
  return (lang) => {
    const cached = cache.get(lang);
    if (cached) return cached;
    const resolved = localizeCopy(tree, lang);
    cache.set(lang, resolved);
    return resolved;
  };
}
